import {
  LLMMessageDTO,
  LLMMessageRole,
  LLMToolCall,
} from '../../../services/backendService/backendService';
import { ToolCallProgress } from '../../dynamicPages/CreatePage/intent-refinement/agentToolCallProgress';

/**
 * Tools the refinement agents call to hand control back to the teacher.
 *
 * All three arrive as ordinary assistant tool calls on the existing message
 * stream — no new socket events. The agent also writes the question (or the
 * reason it wants to bail out) into its streamed reply, so every one of these
 * degrades gracefully: render nothing and the conversation still works.
 */
export const ASK_USER_QUESTIONS_TOOL = 'askUserQuestions'; // intent refinement, one call per lesson
export const ASK_USER_QUESTION_TOOL = 'askUserQuestion'; // lesson refinement, no call limit
export const EXIT_TO_REGENERATE_TOOL = 'exitToRegenerateTheWholeLesson';

/**
 * Prefills the intent refinement composer without submitting it — unlike the
 * existing `prompt` param, which auto-submits. Used to carry
 * `proposedLearningObjective` out of the designer and into a fresh build.
 */
export const PREFILL_PROMPT_QUERY_PARAM = 'prefillPrompt';

/**
 * Card whose intent refinement view we drop the teacher into for a fresh build.
 * It has no prefill copy of its own, so the proposed objective lands in the
 * composer untouched.
 */
const START_NEW_LESSON_CARD_ID = 'generate-lesson';

export function buildStartNewLessonRoute(
  tenantId: string,
  proposedLearningObjective?: string,
) {
  return {
    pathname: '/[tenantId]/home',
    query: {
      tenantId,
      selectedCardId: START_NEW_LESSON_CARD_ID,
      ...(proposedLearningObjective
        ? { [PREFILL_PROMPT_QUERY_PARAM]: proposedLearningObjective }
        : {}),
    },
  };
}

export type AgentQuestion = {
  question: string;
  options: string[];
};

export type PendingAgentQuestions = {
  /** Identifies the tool call so a dismissal survives re-renders. */
  toolCallId: string;
  toolName: string;
  /**
   * Null while the tool call has started but its arguments — the actual
   * questions — haven't streamed in and parsed yet. The drawer renders a
   * loader for that case instead of staying closed.
   */
  questions: AgentQuestion[] | null;
};

export type PendingExitToRegenerate = {
  toolCallId: string;
  reason: string;
  proposedLearningObjective?: string;
};

/**
 * `LLMToolCall.arguments` is typed as `object`, but it round-trips through
 * Postgres JSON and a socket, so treat anything as possible.
 */
function toRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'string') {
    try {
      return toRecord(JSON.parse(value));
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function toOptions(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((option): option is string => typeof option === 'string')
    .map(option => option.trim())
    .filter(option => option.length > 0);
}

function toQuestion(value: unknown): AgentQuestion | null {
  const record = toRecord(value);
  const question = record?.question;
  if (typeof question !== 'string' || !question.trim()) return null;
  return { question: question.trim(), options: toOptions(record?.options) };
}

/**
 * Reads both the plural (`askUserQuestions`) and singular (`askUserQuestion`)
 * shapes into one list. Returns null when the tool call isn't a question or
 * carries nothing renderable.
 */
export function parseAgentQuestionsToolCall(
  toolCall: Pick<LLMToolCall, 'name' | 'arguments'>,
): AgentQuestion[] | null {
  const args = toRecord(toolCall.arguments);
  if (!args) return null;

  if (toolCall.name === ASK_USER_QUESTIONS_TOOL) {
    const rawQuestions = args.questions;
    if (!Array.isArray(rawQuestions)) return null;
    const questions = rawQuestions
      .map(toQuestion)
      .filter((question): question is AgentQuestion => question !== null);
    return questions.length > 0 ? questions : null;
  }

  if (toolCall.name === ASK_USER_QUESTION_TOOL) {
    const question = toQuestion(args);
    return question ? [question] : null;
  }

  return null;
}

export function parseExitToRegenerateToolCall(
  toolCall: Pick<LLMToolCall, 'name' | 'arguments'>,
): Omit<PendingExitToRegenerate, 'toolCallId'> | null {
  if (toolCall.name !== EXIT_TO_REGENERATE_TOOL) return null;
  const args = toRecord(toolCall.arguments);
  if (!args) return null;

  const reason = typeof args.reason === 'string' ? args.reason.trim() : '';
  const proposed =
    typeof args.proposedLearningObjective === 'string'
      ? args.proposedLearningObjective.trim()
      : '';

  if (!reason && !proposed) return null;

  return {
    reason,
    ...(proposed ? { proposedLearningObjective: proposed } : {}),
  };
}

/**
 * Answering is just the next user message on the thread, so a tool call is
 * "pending" exactly while no user message follows it. Walking backwards and
 * stopping at the first user message gives us that for free — no extra state,
 * and the drawer closes itself the moment the answer is sent.
 */
function findPendingToolCall<T>(
  messages: LLMMessageDTO[] | undefined,
  parse: (toolCall: LLMToolCall) => T | null,
): { toolCall: LLMToolCall; parsed: T } | null {
  if (!messages?.length) return null;

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role === LLMMessageRole.USER) return null;
    const toolCalls = message.toolCalls ?? [];
    for (let j = toolCalls.length - 1; j >= 0; j--) {
      const parsed = parse(toolCalls[j]);
      if (parsed !== null) return { toolCall: toolCalls[j], parsed };
    }
  }

  return null;
}

export function findPendingAgentQuestions(
  messages: LLMMessageDTO[] | undefined,
): PendingAgentQuestions | null {
  const match = findPendingToolCall(messages, parseAgentQuestionsToolCall);
  if (!match) return null;
  return {
    // `callId`, not the message-local `id`: it's the same identifier the live
    // `stream:toolCall` signal carries, so a call started as a
    // `findLiveAgentQuestionsCall` loader keeps the exact same key once this
    // function starts resolving it instead — the drawer swaps in place
    // rather than unmounting and remounting.
    toolCallId: match.toolCall.callId,
    toolName: match.toolCall.name,
    questions: match.parsed,
  };
}

/**
 * A live `askUserQuestions`/`askUserQuestion` call the teacher hasn't seen a
 * drawer for yet. Needed because the tool's own `completed` signal on
 * `stream:toolCall` arrives almost immediately — well before the message
 * carrying its parsed arguments reaches history, which only catches up once
 * the *whole* turn (every further tool call, then the full streamed reply)
 * has finished. Matching `completed` here, not just `started`, is what closes
 * that entire gap: without it, the drawer has nothing to show from the moment
 * the call finishes until the agent stops talking, which is the original bug
 * this exists to fix.
 *
 * Excludes anything `settledInHistory` — a callId that already showed up in
 * a message's `toolCalls`, whether or not it turned out to still be pending —
 * so a question from an earlier turn that the teacher already answered can't
 * get resurrected here. `failed` is excluded too: nothing renderable is ever
 * coming for it.
 */
export function findLiveAgentQuestionsCall(
  progress: ToolCallProgress[],
  historyToolCalls: LLMToolCall[],
): Pick<PendingAgentQuestions, 'toolCallId' | 'toolName'> | null {
  const settledInHistory = new Set(
    historyToolCalls.map(toolCall => toolCall.callId).filter(Boolean),
  );
  const liveCall = progress.find(
    entry =>
      entry.status !== 'failed' &&
      !settledInHistory.has(entry.toolCallId) &&
      (entry.toolName === ASK_USER_QUESTIONS_TOOL ||
        entry.toolName === ASK_USER_QUESTION_TOOL),
  );
  return liveCall
    ? { toolCallId: liveCall.toolCallId, toolName: liveCall.toolName }
    : null;
}

export function findPendingExitToRegenerate(
  messages: LLMMessageDTO[] | undefined,
): PendingExitToRegenerate | null {
  const match = findPendingToolCall(messages, parseExitToRegenerateToolCall);
  if (!match) return null;
  return { toolCallId: match.toolCall.callId, ...match.parsed };
}

/**
 * Builds the next user message from what the teacher answered. Raw text, sent
 * through the normal message path — the agent gets no special payload.
 *
 * One `Q:`/`A:` block per answered question, so the agent can always tell which
 * answer belongs to which question. Skipped questions are simply left out.
 */
export function buildAgentQuestionsAnswerMessage(
  questions: AgentQuestion[],
  answers: Record<number, string>,
): string {
  return questions
    .map((question, index) => ({ question, answer: (answers[index] ?? '').trim() }))
    .filter(entry => entry.answer.length > 0)
    .map(({ question, answer }) => `Q: ${question.question}\nA: ${answer}`)
    .join('\n\n');
}
